/* @flow */

import * as React from 'react';
import { branch, compose, renderNothing, withProps } from 'recompose';
import glamorous from 'glamorous';
import AnimatedGroup from 'app/components/commons/AnimatedGroup';
import AnimatedGroupChild from 'app/components/commons/AnimatedGroupChild';
import BouncyLoader from 'app/components/commons/BouncyLoader';
import CircleButton from 'app/components/buttons/CircleButton';
import Icon from 'app/components/commons/Icon';
import * as Enter from 'app/utils/animations/enter';
import * as Leave from 'app/utils/animations/leave';
import { NOT_FRIEND, THEY_REQUESTED, YOU_REQUESTED } from 'app/constants/friendStatusTypes';
import t from 'app/utils/translate';
import withHover from 'app/hoc/withHover';

type Props = {
  buttonKind: string,
  buttonType: string,
  iconType: string,
  isLoading: boolean,
  message?: string,
  onClick?: () => void,
  onMouseEnter?: () => void,
  onMouseLeave?: () => void,
  style?: Object
};

const defaultProps = {
  message: undefined,
  onClick: undefined,
  onMouseEnter: undefined,
  onMouseLeave: undefined,
  style: undefined
};

const StyledAnimatedGroup = glamorous(AnimatedGroup)({
  alignItems: 'center',
  display: 'flex'
});

const StyledAnimatedGroupChild = glamorous(AnimatedGroupChild, {
  withProps: {
    onEnter: Enter.slideLeftAndGrow(0.2, 0.2),
    onLeave: Leave.slideRightAndShrink(0.2)
  }
})({
  flexGrow: 1,
  marginRight: 10
});

const StyledBouncyLoader = glamorous(BouncyLoader)({
  paddingBottom: 'calc(33% - 4px)',
  width: 'calc(100% - 8px)'
});

const StyledCircleButton = glamorous(CircleButton, {
  withProps: {
    onEnter: Enter.appear(0.2, 0.2),
    onLeave: Leave.disappear(0.2)
  }
})({
  height: 45,
  width: 45
});

function FriendRowButton({
  buttonKind,
  buttonType,
  iconType,
  isLoading,
  message,
  onClick,
  onMouseEnter,
  onMouseLeave,
  style
}: Props) {
  return (
    <StyledAnimatedGroup>
      <StyledCircleButton
        key={buttonType}
        kind={buttonKind}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        style={style}
      >
        {isLoading ? <StyledBouncyLoader /> : <Icon type={iconType} />}
      </StyledCircleButton>
      {message && <StyledAnimatedGroupChild>{message}</StyledAnimatedGroupChild>}
    </StyledAnimatedGroup>
  );
}

FriendRowButton.defaultProps = defaultProps;

const enhance = compose(
  withHover,
  branch(
    props => props.addedToGroup,
    withProps(() => ({
      buttonKind: 'success',
      buttonType: 'addedToGroup',
      iconType: 'icon-tick',
      isLoading: false,
      message: t('Added'),
      style: { fontSize: 10 }
    }))
  ),
  branch(
    props => props.friendRequestSent,
    withProps(() => ({
      buttonKind: 'success',
      buttonType: 'friendRequestSent',
      iconType: 'icon-tick',
      isLoading: false,
      message: t('Friend request sent and added to the group'),
      style: { fontSize: 10 }
    }))
  ),
  branch(
    props => props.removedFriend,
    withProps(() => ({
      buttonKind: 'success',
      buttonType: 'removedFriend',
      iconType: 'icon-tick',
      isLoading: false,
      message: t('Removed'),
      style: { fontSize: 10 }
    }))
  ),
  branch(
    props => props.isFriendsGroup && props.friendStatus === NOT_FRIEND,
    withProps(props => ({
      buttonKind: 'primary',
      buttonType: 'friendRequest',
      isLoading: props.isCreatingFriendRequest,
      onClick: props.onCreateFriendRequest,
      style: { fontSize: 14 },
      type: 'icon-invite-friend'
    })),
    branch(
      props =>
        !props.isFriendsGroup &&
        props.friendStatus !== THEY_REQUESTED &&
        props.friendStatus !== YOU_REQUESTED,
      branch(
        props => props.hasFriend,
        withProps(props => ({
          buttonKind: props.hover ? 'grey' : 'success',
          buttonType: 'removeFriend',
          iconType: props.hover ? 'icon-minus' : 'icon-tick',
          isLoading: props.isSaving,
          onClick: props.onRemoveFriend,
          style: { fontSize: 10 }
        })),
        withProps(props => ({
          buttonKind: 'primary',
          buttonType: 'addToGroup',
          iconType: 'icon-plus',
          isLoading: props.isSaving || props.isCreatingFriendRequest,
          onClick: props.onAddToGroup,
          style: { fontSize: 10 }
        }))
      ),
      renderNothing
    )
  )
);

export default enhance(FriendRowButton);
